import React, { useState, useEffect } from 'react'
import GoldSiverHeader from 'view/HomePage/GoldSiverHeader'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { getProfile } from '../../../store/auth'
import axiosInstance from '../../../api/axios'
import { API_ENDPOINTS } from '../../../api/endpoint'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

function Pockemart() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const userProfile = useSelector(state => state.auth.user);
    const userData = useSelector(state => state.auth.user_data);
    const [selected, setSelected] = useState('pokeballs');
    const [items, setItems] = useState({
        pokeballs: [],
        potions: [],
        key_items: [],
        special_items: [],
        stones: [],
        attacks: []
    });
    const [loading, setLoading] = useState(false);
    const [quantities, setQuantities] = useState({}); // {item_name: quantity}

    useEffect(() => {
        dispatch(getProfile());
        loadItems();
    }, [dispatch]);

    const loadItems = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.GET_SHOP_ITEMS);
            console.log('Shop items response:', response.data);
            
            if (response.data && response.data.success) {
                const loadedItems = response.data.items || {
                    pokeballs: [],
                    potions: [],
                    key_items: [],
                    special_items: [],
                    stones: [],
                    attacks: []
                };
                
                // Debug: Log first item to see price structure
                if (loadedItems.pokeballs && loadedItems.pokeballs.length > 0) {
                    console.log('First pokeball item:', loadedItems.pokeballs[0]);
                    console.log('Has silver:', loadedItems.pokeballs[0].silver);
                    console.log('Has gold:', loadedItems.pokeballs[0].gold);
                }
                if (response.data.debug) {
                    console.log('Debug info:', response.data.debug);
                }
                
                console.log('Loaded items:', loadedItems);
                setItems(loadedItems);
            } else {
                console.error('Shop items response error:', response.data);
                toast.error(response.data?.error || t('Failed to load shop items'));
            }
        } catch (error) {
            console.error('Error loading shop items:', error);
            console.error('Error response:', error?.response?.data);
            toast.error(error?.response?.data?.error || t('Failed to load shop items'));
        } finally {
            setLoading(false);
        }
    };

    const handleQuantityChange = (itemName, change) => {
        setQuantities(prev => {
            const current = prev[itemName] || 1;
            const newQuantity = Math.max(1, current + change);
            return { ...prev, [itemName]: newQuantity };
        });
    };

    const handleBuyItem = async (item) => {
        const quantity = quantities[item.naam] || 1;
        
        // Check multiple auth states - try userProfile first, then userData
        const currentUser = userProfile || userData;
        
        if (!currentUser) {
            // Try to refresh profile
            try {
                await dispatch(getProfile()).unwrap();
                // After refresh, check again from store
                const state = store.getState();
                const refreshedUser = state.auth.user || state.auth.user_data;
                if (!refreshedUser) {
                    toast.error(t('Please login to buy items'));
                    return;
                }
            } catch (error) {
                toast.error(t('Please login to buy items'));
                return;
            }
        }

        // Use currentUser for money check - get fresh state from store
        const state = store.getState();
        const finalUser = userProfile || userData || state.auth.user || state.auth.user_data;
        if (!finalUser) {
            toast.error(t('Please login to buy items'));
            return;
        }

        // Check if user has enough money
        const totalSilver = (item.silver || 0) * quantity;
        const totalGold = (item.gold || 0) * quantity;
        const userSilver = finalUser.silver || 0;
        const userGold = finalUser.gold || 0;

        if (totalSilver > 0 && userSilver < totalSilver) {
            toast.error(t('Not enough silver'));
            return;
        }

        if (totalGold > 0 && userGold < totalGold) {
            toast.error(t('Not enough gold'));
            return;
        }

        try {
            const response = await axiosInstance.post(API_ENDPOINTS.BUY_ITEM, {
                item_name: item.naam,
                quantity: quantity
            });

            if (response.data && response.data.success) {
                toast.success(t('Successfully purchased {{quantity}}x {{item}}!', {
                    quantity: quantity,
                    item: item.naam
                }));
                
                // Refresh user profile to update money
                dispatch(getProfile());
                
                // Reset quantity for this item
                setQuantities(prev => {
                    const newQuantities = { ...prev };
                    delete newQuantities[item.naam];
                    return newQuantities;
                });
            } else {
                toast.error(response.data?.error || t('Failed to purchase item'));
            }
        } catch (error) {
            console.error('Error buying item:', error);
            toast.error(error?.response?.data?.error || t('Failed to purchase item'));
        }
    };

    // Mapping between DB item names and actual file names (for items with spaces or different names)
    // This handles cases where DB has "Poke ball" but file is "Pokeball.png"
    const itemNameMapping = {
        'Poke ball': 'Pokeball',
        'DNA ball': 'DNAball', // File is "DNAball.png" but DB might have "DNA ball"
        // Add more mappings here as needed
    };

    const getItemImage = (itemName) => {
        // Normalize the item name
        let normalizedName = itemName.trim();
        
        // Check if there's a mapping for this item name
        if (itemNameMapping[normalizedName]) {
            normalizedName = itemNameMapping[normalizedName];
            console.log(`🔄 [Pockemart] Mapped "${itemName}" to "${normalizedName}"`);
        }
        
        // Use process.env.PUBLIC_URL if available (for production builds)
        // In development, this will be empty string, so we use direct path
        const basePath = process.env.PUBLIC_URL || '';
        
        // Replace spaces with underscores to match file names
        // Files have been renamed: "Poke ball.png" -> "Poke_ball.png"
        const fileName = normalizedName.replace(/\s+/g, '_');
        const imagePath = `${basePath}/images/items/${fileName}.png`;
        console.log(`🖼️ [Pockemart] Image path for "${itemName}": ${imagePath} (original: "${normalizedName}")`);
        return imagePath;
    };

    const getCategoryName = (category) => {
        const names = {
            'pokeballs': t('Pokeballs'),
            'potions': t('Potions'),
            'key_items': t('Key Items'),
            'special_items': t('Special Items'),
            'stones': t('Stones'),
            'attacks': t('Attacks')
        };
        return names[category] || category;
    };

    return (
        <div>
            <style>{`
                .pockemart-silver-icon {
                    position: absolute;
                    bottom: 18px;
                    left: 16px;
                    width: 20px;
                }
            `}</style>
            <GoldSiverHeader previous='/city' title='PokéMart'>
                <section className="ar_shop_area_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_shop_area">
                                    <div className="ar_shop_area_left">
                                        <div onClick={() => setSelected('pokeballs')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'pokeballs' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'pokeballs' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Pokeballs')}</p></a>
                                            </div>
                                        </div>
                                        <div onClick={() => setSelected('potions')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'potions' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'potions' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Potions')}</p></a>
                                            </div>
                                        </div>
                                        <div onClick={() => setSelected('key_items')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'key_items' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'key_items' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Key Items')}</p></a>
                                            </div>
                                        </div>
                                        <div onClick={() => setSelected('special_items')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'special_items' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'special_items' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Special Items')}</p></a>
                                            </div>
                                        </div>
                                        <div onClick={() => setSelected('stones')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'stones' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'stones' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Stones')}</p></a>
                                            </div>
                                        </div>
                                        <div onClick={() => setSelected('attacks')} style={{ cursor: 'pointer' }}>
                                            <div className={`ar_shop_nav_single ${selected === 'attacks' ? '' : 'topup'}`}>
                                                <a><img src={`images/shop/${selected === 'attacks' ? 'gridShop' : 'topUp'}.png`} alt="" /></a>
                                                <a><p>{t('Attacks')}</p></a>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ar_shop_area_right">
                                        <div className="ar_shop_item_wrapper p-5">
                                            {loading ? (
                                                <div className="text-center text-light">{t('Loading items...')}</div>
                                            ) : items[selected] && items[selected].length > 0 ? (
                                                items[selected].map((item, index) => {
                                                    const quantity = quantities[item.naam] || 1;
                                                    const totalSilver = (item.silver || 0) * quantity;
                                                    const totalGold = (item.gold || 0) * quantity;
                                                    
                                                    return (
                                                        <div className="ar_shop_single_item" key={index} style={{ position: 'relative' }}>
                                                            <img src="/images/shop/item.png" alt="" />
                                                            <div className="ar_shop_in_cont">
                                                                <img 
                                                                    src={getItemImage(item.naam)} 
                                                                    alt={item.naam}
                                                                    onError={(e) => {
                                                                        console.error("❌ Image failed to load:", getItemImage(item.naam), "Item name:", item.naam);
                                                                        
                                                                        // Try with original name (with spaces) as fallback
                                                                        const basePath = process.env.PUBLIC_URL || '';
                                                                        let normalizedName = item.naam.trim();
                                                                        if (itemNameMapping[normalizedName]) {
                                                                            normalizedName = itemNameMapping[normalizedName];
                                                                        }
                                                                        // Try with original name (with spaces) - maybe file wasn't renamed
                                                                        const originalPath = `${basePath}/images/items/${normalizedName}.png`;
                                                                        console.log(`🔄 [Pockemart] Trying original path with spaces: ${originalPath}`);
                                                                        
                                                                        // Store original src to check if we've tried all fallbacks
                                                                        const originalSrc = e.target.src;
                                                                        
                                                                        e.target.src = originalPath;
                                                                        e.target.onerror = () => {
                                                                            // Last resort: try with encoded spaces
                                                                            const encodedPath = `${basePath}/images/items/${encodeURIComponent(normalizedName)}.png`;
                                                                            console.log(`🔄 [Pockemart] Trying encoded path: ${encodedPath}`);
                                                                            e.target.src = encodedPath;
                                                                            e.target.onerror = () => {
                                                                                // All fallbacks failed - use loading.gif as default (handles 403 and other errors)
                                                                                console.log("🔄 [Pockemart] All fallbacks failed, using loading.gif as default");
                                                                                e.target.onerror = null;
                                                                                e.target.src = '/images/loading.gif';
                                                                            };
                                                                        };
                                                                    }}
                                                                    onLoad={() => {
                                                                        console.log(`✅ [Pockemart] Image loaded successfully: ${item.naam}`);
                                                                    }}
                                                                />
                                                                <p>{item.naam}</p>
                                                            </div>
                                                            <div className="ar_shop_bottom_cont">
                                                                {item.silver !== undefined && item.silver !== null && item.silver > 0 && (
                                                                    <p>
                                                                        {totalSilver}
                                                                    </p>
                                                                )}
                                                                {item.gold !== undefined && item.gold !== null && item.gold > 0 && (
                                                                    <p>
                                                                        {totalGold}
                                                                    </p>
                                                                )}
                                                                {(!item.silver || item.silver === 0) && (!item.gold || item.gold === 0) && (
                                                                    <p style={{ color: '#ffc323' }}>{t('Free')}</p>
                                                                )}
                                                            </div>
                                                            {item.silver > 0 && (
                                                                <div className='silver pockemart-silver-icon'>
                                                                    <img src="/images/mock-05.png" alt="" />
                                                                </div>
                                                            )}
                                                            <div className='button-group'>
                                                                <span className='cursor-pointer' onClick={() => handleQuantityChange(item.naam, -1)}>-</span>
                                                                <span className='qty'>{quantity}</span>
                                                                <span className='cursor-pointer' onClick={() => handleQuantityChange(item.naam, 1)}>+</span>
                                                            </div>
                                                            <button 
                                                                className='btn btn-primary mt-2 w-100 ar_shop_buy_button'
                                                                onClick={() => handleBuyItem(item)}
                                                                aria-label={t('Buy')}
                                                            >
                                                            </button>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="text-center text-light">{t('No items available in this category')}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </GoldSiverHeader>
        </div>
    )
}

export default Pockemart
